import apiClient from './api';

export interface InboxConversation {
  id: string;
  status: string;
  channel_type: string;
  customer: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    contact_id?: string;
    channel_type: string;
    external_id: string;
  };
  last_message_preview: string;
  last_customer_message_at: string;
  last_human_reply?: {
    sender_name: string;
    created_at: string;
  } | null;
  assigned_to?: {
    user_id: string;
    user_name: string;
  } | null;
  priority?: string | null;
  metadata: Record<string, unknown>;
}

export interface ChannelAvailability {
  channel_type: string;
  channel_id: string;
  customer_id: string;
  external_id: string;
  available: boolean;
  reason?: string;
  last_customer_message_at?: string;
}

export const inboxService = {
  async getConversations(params: {
    filter?: string;
    channel_type?: string;
    limit?: number;
    page?: number;
  }): Promise<{ conversations: InboxConversation[]; total: number; page: number }> {
    const query = new URLSearchParams();
    if (params.filter) query.set('filter', params.filter);
    if (params.channel_type) query.set('channel_type', params.channel_type);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.page) query.set('page', String(params.page));
    const qs = query.toString();
    const { data } = await apiClient.get(`/inbox/conversations${qs ? `?${qs}` : ''}`);
    return data;
  },

  async getChannelAvailability(customerId: string): Promise<{ channels: ChannelAvailability[] }> {
    const { data } = await apiClient.get(`/inbox/customer/${customerId}/channels`);
    return data;
  },

  async assignConversation(conversationId: string, userId: string | null): Promise<{ success: boolean; assigned_to: { user_id: string; user_name: string } | null }> {
    const { data } = await apiClient.post(`/inbox/conversations/${conversationId}/assign`, { user_id: userId });
    return data;
  },

  async createNote(conversationId: string, content: string): Promise<any> {
    const { data } = await apiClient.post(`/conversations/${conversationId}/notes`, { content });
    return data?.data || data;
  },

  async sendHumanMessage(conversationId: string, content: string, channelId?: string): Promise<any> {
    const { data } = await apiClient.post(`/conversations/${conversationId}/human-message`, {
      content,
      channel_id: channelId,
    });
    return data?.data || data;
  },

  async updateConversationStatus(conversationId: string, status: string): Promise<any> {
    const { data } = await apiClient.post(`/conversations/${conversationId}/status`, { status });
    return data?.data || data;
  },

  async updateCustomerProfile(customerId: string, profileData: { name?: string; email?: string; phone?: string }): Promise<any> {
    const { data } = await apiClient.patch(`/customers/${customerId}`, profileData);
    return data?.data || data;
  },

  async updateCustomerTags(customerId: string, tags: {
    is_high_potential?: boolean;
    is_blocked?: boolean;
    custom_tags?: string[];
  }): Promise<any> {
    const { data } = await apiClient.patch(`/customers/${customerId}/tags`, tags);
    return data?.data || data;
  },
};
